import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { formatDisplayLabel } from '../utils/camelCaseLabel';
import { useAppColors } from '../context/PreferencesContext';
import {
  RequestFormChevronIcon,
  RequestFormLabel,
} from './RequestFormField';

export type RequestFormSelectOption = {
  value: string;
  label: string;
};

type Props = {
  label: string;
  required?: boolean;
  value: string;
  options: RequestFormSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  editable?: boolean;
};

/**
 * Web apply-form select. Leave types are native `<option>`s on this field.
 */
export function RequestFormSelectField({
  label,
  required,
  value,
  options,
  onChange,
  placeholder = 'Select',
  editable = true,
}: Props) {
  const palette = useAppColors();
  const selected = options.find(option => option.value === value);
  const displayValue = selected
    ? formatDisplayLabel(selected.label)
    : placeholder;
  const disabled = !editable || options.length === 0;

  return (
    <View style={styles.container}>
      <RequestFormLabel label={label} required={required} />
      <View
        style={[
          styles.field,
          disabled && styles.fieldLocked,
          {
            borderColor: palette.border,
            backgroundColor: disabled ? palette.background : palette.surface,
          },
        ]}
      >
        <Text
          pointerEvents="none"
          style={[
            styles.input,
            {
              color: selected ? palette.textPrimary : palette.textMuted,
            },
          ]}
        >
          {displayValue}
        </Text>
        <View pointerEvents="none">
          <RequestFormChevronIcon color={palette.textMuted} />
        </View>
        {React.createElement(
          'select',
          {
            'aria-label': label,
            accessibilityLabel: label,
            value,
            disabled,
            onChange: (event: { target?: { value?: string } }) => {
              onChange(String(event.target?.value ?? ''));
            },
            style: {
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.01,
              borderWidth: 0,
              backgroundColor: 'transparent',
              cursor: 'pointer',
            },
          },
          React.createElement('option', { value: '', disabled: true }, placeholder),
          ...options.map(option =>
            React.createElement(
              'option',
              { key: option.value, value: option.value },
              formatDisplayLabel(option.label),
            ),
          ),
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  field: {
    minHeight: 50,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    position: 'relative',
    overflow: 'hidden',
  },
  fieldLocked: {
    opacity: 0.92,
  },
  input: {
    ...typography.input,
    flex: 1,
    paddingVertical: spacing.md,
  },
});
